/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.docking.js

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.docking.Dockable
import com.macrofocus.docking.DockingBar
import js.objects.unsafeJso
import org.mkui.component.CPComponent
import org.mkui.component.ReactComponent
import org.mkui.react.useProperty
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.*

/**
 * Dockable decorated with a title bar, the React counterpart of the Swing `SwingDockable`. The title bar shows the
 * title and, once the dockable is attached to a docking bar, a "Float" and a "Hide" button.
 */
class JsDecoratedDockable(
    private val insideComponent: CPComponent,
    override val shortTitle: String?,
    override val title: String?,
    override val description: String?,
    override val iconURL: String?,
) : Dockable<CPComponent> {
    private val dockingBarProperty: MutableProperty<DockingBar<CPComponent>?> = SimpleProperty(null)
    private val detachedProperty: MutableProperty<Boolean> = SimpleProperty(false)

    override var dockingBar: DockingBar<CPComponent>? by dockingBarProperty

    override val container: CPComponent
        get() = insideComponent

    // Inside the DOM builders `title` is the HTML attribute of the element being built
    private val dockableTitle: String?
        get() = title

    override fun detachContainer(): CPComponent {
        detachedProperty.value = true
        return insideComponent
    }

    override fun attachContainer() {
        detachedProperty.value = false
    }

    override fun dispose() {
        dockingBar = null
    }

    override val component: CPComponent = ReactComponent(FC<Props> {
        val (dockingBar, _) = useProperty(dockingBarProperty)
        val (detached, _) = useProperty(detachedProperty)

        div {
            className = ClassName("DecoratedDockable")
            asDynamic()["data-testid"] = "Dockable:$dockableTitle"
            style = unsafeJso {
                position = Position.relative
                display = Display.flex
                flexDirection = FlexDirection.column
                boxSizing = BoxSizing.borderBox
                width = 100.pct
                height = 100.pct
                border = Border(1.px, LineStyle.solid, BorderColor)
                overflow = Overflow.hidden
            }

            div {
                className = ClassName("DecoratedDockable-title")
                asDynamic()["data-testid"] = "TitleBar:$dockableTitle"
                style = unsafeJso {
                    display = Display.flex
                    alignItems = AlignItems.center
                    flexShrink = number(0.0)
                    boxSizing = BoxSizing.borderBox
                    height = TitleBarHeight.px
                    padding = Padding(0.px, 5.px)
                    borderBottom = Border(1.px, LineStyle.solid, BorderColor)
                    backgroundImage = "linear-gradient(rgb(248, 248, 248), rgb(227, 227, 227))".unsafeCast<BackgroundImage>()
                    fontSize = 12.px
                    userSelect = None.none
                }

                span {
                    style = unsafeJso {
                        flexGrow = number(1.0)
                        overflow = Overflow.hidden
                        whiteSpace = WhiteSpace.nowrap
                    }
                    +(dockableTitle ?: "")
                }

                val bar = dockingBar
                if (bar != null) {
                    titleButton("Float", floating = true) { bar.setFloating(this@JsDecoratedDockable, true) }
                    titleButton("Hide", floating = false) { bar.setActive(this@JsDecoratedDockable, false) }
                }
            }

            div {
                className = ClassName("DecoratedDockable-content")
                style = unsafeJso {
                    position = Position.relative
                    flexGrow = number(1.0)
                    minHeight = 0.px
                    overflow = Overflow.hidden
                }
                if (!detached) {
                    insideComponent.nativeComponent()
                }
            }
        }
    })

    private fun ChildrenBuilder.titleButton(name: String, floating: Boolean, onClick: () -> Unit) {
        div {
            asDynamic()["data-testid"] = "TitleButton:$dockableTitle:$name"
            this.title = name
            style = unsafeJso {
                position = Position.relative
                width = 14.px
                height = 14.px
                marginLeft = 2.px
                cursor = Cursor.pointer
            }
            this.onClick = { event ->
                event.stopPropagation()
                onClick()
            }
            if (floating) {
                // Two overlapping windows
                iconBox(top = 1, left = 5)
                iconBox(top = 5, left = 1)
            } else {
                // Minimize bar
                div {
                    style = unsafeJso {
                        position = Position.absolute
                        left = 2.px
                        right = 2.px
                        bottom = 2.px
                        height = 2.px
                        backgroundColor = IconColor
                    }
                }
            }
        }
    }

    private fun ChildrenBuilder.iconBox(top: Int, left: Int) {
        div {
            style = unsafeJso {
                position = Position.absolute
                this.top = top.px
                this.left = left.px
                width = 6.px
                height = 6.px
                border = Border(1.px, LineStyle.solid, IconColor)
                backgroundColor = rgb(240, 240, 240)
            }
        }
    }

    companion object {
        const val TitleBarHeight = 22
        private val BorderColor = rgb(142, 142, 142)
        private val IconColor = rgb(90, 90, 90)
    }
}
